import api from '../utils/api';

class SubscriptionService {
  // ───────────────────────── Public Endpoints ─────────────────────────
  
  // List all available subscription plans (public endpoint)
  async listPlans() {
    try {
      const response = await api.get('/subscriptions/plans');
      // Backend returns ApiEnvelope<List<SubscriptionPlanDto>>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get user's current subscription snapshot
  async getUserSubscription(userId) {
    try {
      const response = await api.get(`/subscriptions/${userId}`);
      // Backend returns ApiEnvelope<SubscriptionDto> or 404
      return response.data;
    } catch (error) {
      // Handle 404 case where user has no subscription
      if (error.response?.status === 404) {
        return { success: false, data: null, message: 'No subscription found' };
      }
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Start a free subscription for a user
  async startFreeSubscription(userId, planCode = 'FREE') {
    try {
      const response = await api.post(`/subscriptions/${userId}/start-free?planCode=${planCode}`);
      // Backend returns ApiEnvelope<SubscriptionDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ───────────────────────── Admin Endpoints ─────────────────────────

  // Change user's subscription plan (admin only)
  async changePlan(userId, planCode) {
    try {
      const response = await api.post(`/subscriptions/${userId}/change-plan`, {
        planCode
      });
      // Backend returns ApiEnvelope<SubscriptionDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ───────────────────────── Billing Endpoints (Admin Only) ─────────────────────────

  // Start a paid subscription with billing (admin only)
  async startBillingSubscription(billingData) {
    try {
      const response = await api.post('/subscriptions/billing/start', billingData);
      // Backend returns ApiEnvelope<SubscriptionChargeResult>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Cancel a billing subscription (admin only)
  async cancelBillingSubscription(stripeSubId, userId) {
    try {
      await api.post(`/subscriptions/billing/${stripeSubId}/cancel?userId=${userId}`);
      // Backend returns void (204 No Content)
      return { success: true };
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Set auto-renew for a billing subscription (admin only)
  async setBillingAutoRenew(stripeSubId, enabled) {
    try {
      await api.put(`/subscriptions/billing/${stripeSubId}/auto-renew?enabled=${enabled}`);
      // Backend returns void (204 No Content)
      return { success: true };
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ───────────────────────── Admin Subscription Management ─────────────────────────

  // Start trial subscription (admin endpoint)
  async startTrial(userId, planCode) {
    try {
      const response = await api.post('/admin/subscriptions/start-trial', { userId, planCode });
      // Backend returns ApiEnvelope<SubscriptionDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Upgrade subscription (admin endpoint)
  async upgradeSubscription(userId, planCode) {
    try {
      const response = await api.post('/admin/subscriptions/upgrade', { userId, planCode });
      // Backend returns ApiEnvelope<SubscriptionDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Cancel subscription (admin endpoint)
  async cancelSubscription(userId) {
    try {
      await api.post(`/admin/subscriptions/${userId}/cancel`);
      // Backend returns void (204 No Content)
      return { success: true };
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ───────────────────────── Feature Configuration (Admin Only) ─────────────────────────

  // Get subscription feature configuration
  async getFeatureConfig() {
    try {
      const response = await api.get('/admin/features/subscriptions');
      // Backend returns ApiEnvelope<FeatureState>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Update subscription feature configuration
  async updateFeatureConfig(config) {
    try {
      const response = await api.put('/admin/features/subscriptions', config);
      // Backend returns ApiEnvelope<FeatureState>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ───────────────────────── Utility Methods ─────────────────────────

  // Helper method to extract error message from backend ApiEnvelope
  _extractErrorMessage(error) {
    // Backend uses ApiEnvelope structure: { success: boolean, data: T, message?: string }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    // Handle specific HTTP status codes
    if (error.response?.status) {
      switch (error.response.status) {
        case 400:
          return 'Bad request - please check your input';
        case 401:
          return 'Unauthorized - please log in';
        case 403:
          return 'Forbidden - insufficient permissions';
        case 404:
          return 'Resource not found';
        case 500:
          return 'Internal server error';
        default:
          return `HTTP ${error.response.status} error`;
      }
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred';
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
